import type { paths } from '@/types/api'
import { fetchWithAuth,handleApiError } from '@/utils/api-error-handler'

type ChatHistory =
  paths['/v1/agent/chat/history/{chat_id}']['get']['responses']['default']['content']['200']
type NewChatHistory =
  paths['/v1/agent/chat/history']['post']['requestBody']['content']['application/json']
type UpdateChatHistory =
  paths['/v1/agent/chat/history/{chat_id}']['put']['requestBody']['content']['application/json']
type ChatsHistory =
  paths['/v1/agent/chat/history']['get']['responses']['default']['content']['200']

export type ChatsHistoryListItem = {
  id: string
  title?: string | null
  project_name?: string | null
  can_write?: boolean | null
  executions?: number | null
  created_at?: string | null
  updated_at?: string | null
  status?: string | null
  organize_label?: 'pinned' | 'archive' | null
  [key: string]: unknown
}

export type ChatsHistoryPage = {
  data: ChatsHistoryListItem[]
  next_cursor: string | null
}

export type AgentChatSummary = {
  id: string
  title: string
  created_at: string
  updated_at: string
  user_id: string
  status: string
  organize_label?: 'pinned' | 'archive' | null
  executions: number
}

export type ChatHistorySearchResponse = {
  q: string
  count: number
  results: ChatsHistoryListItem[]
}

export type WorkflowsListItem = {
  status: string
  workflow_id: string
  workflow_type: string
  created_at: string
  updated_at: string
  running_started_at?: string | null
  queued_at?: string | null
  queue_age_seconds?: number | null
  user_id: string
  project_id?: string | null
  candidates_count?: number | null
  label?: string | null
  user_name: string
  user_avatar_url?: string | null
  runtime_in_seconds: number | null
  billable_runtime_in_seconds?: number | null
  target_id?: string | null
  target_name?: string | null
  library_item_id?: string | null
  app_name?: string | null
  app_avatar_url?: string | null
  app_version?: string | null
}

export type AppCatalogListItem = {
  app_key: string
  name: string
  version?: string | null
  avatar_url?: string | null
  kind?: 'model' | 'workflow' | 'tool' | string | null
}

export type WorkflowsListResponse = {
  data: WorkflowsListItem[]
  count: number
  next_cursor: string | null
}

export type LibraryListResponse = {
  root: any[]
  count: number
  next_cursor: string | null
}

export type LibraryItem = {
  id: string
  name: string
  type: 'folder' | 'file'
  fileType?: string | null
  origin?: string | null
  size?: number | null
  metadata?: Record<string, unknown> | null
  children?: LibraryItem[] | null
  colorHex?: string | null
  createdAt?: string
}

export type CreateLibraryItemResult = {
  item?: LibraryItem
  readOnlyDenied?: boolean
}

export class ReadOnlyTeamDeniedError extends Error {
  constructor() {
    super('READ_ONLY_TEAM_DENIED')
    this.name = 'ReadOnlyTeamDeniedError'
  }
}

export const isReadOnlyTeamDeniedError = (error: unknown): error is ReadOnlyTeamDeniedError =>
  error instanceof ReadOnlyTeamDeniedError || (error instanceof Error && error.name === 'ReadOnlyTeamDeniedError')

export type LibrarySearchResult = {
  id: string
  name: string
  type: 'folder' | 'file'
  fileType?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  parentId?: string | null
  parentName?: string | null
  metadata?: any | null
  colorHex?: string | null
}

export type LibrarySearchResponse = {
  q: string
  count: number
  results: LibrarySearchResult[]
}

export type ProjectTarget = {
  id: string
  project_id: string
  target_name: string
  library_item_id: string
  created_at: string
  updated_at: string
}

export type AddProjectTargetResult = {
  ok: boolean
  readOnlyDenied?: boolean
}

export type ProjectPickerItem = {
  id: string
  title: string
  updated_at?: string | null
}

const API_URL = process.env.NEXT_PUBLIC_API_URL
const PROJECT_BY_ID_CACHE = new Map<string, ProjectPickerItem>()
const PROJECT_BY_ID_INFLIGHT = new Map<string, Promise<ProjectPickerItem | undefined>>()

export const getProjectTarget = async (targetId: string): Promise<ProjectTarget | undefined> => {
  try {
    const response = await fetchWithAuth(`${API_URL}/v1/projects/targets/${encodeURIComponent(targetId)}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' }
    })
    if (!response.ok) return undefined
    const json = await response.json()
    return json as ProjectTarget
  } catch (error) {
    console.error('Error fetching project target:', error)
    await handleApiError(error)
    return undefined
  }
}

export const addProjectTarget = async (
  projectId: string,
  payload: { target_name: string; library_item_id: string; file_name: string }
): Promise<AddProjectTargetResult> => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/v1/projects/${encodeURIComponent(projectId)}/targets`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }
    )
    if (!response.ok) {
      if (response.status === 403) {
        try {
          const body = (await response.clone().json()) as { message?: string } | null
          const message = String(body?.message || '').toLowerCase()
          if (message.includes('read-only permission for this team')) {
            return { ok: false, readOnlyDenied: true }
          }
        } catch {
          // ignore non-JSON 403 payloads
        }
      }
      return { ok: false }
    }
    return { ok: true }
  } catch (error) {
    console.error('Error adding project target:', error)
    await handleApiError(error)
    return { ok: false }
  }
}

export const searchProjects = async (
  q?: string,
  opts?: { limit?: number; signal?: AbortSignal }
): Promise<ProjectPickerItem[]> => {
  try {
    if (!API_URL) return []
    const params = new URLSearchParams()
    params.set('limit', String(Math.min(50, Math.max(1, opts?.limit ?? 20))))
    const trimmed = String(q || '').trim()
    if (trimmed.length >= 1) params.set('q', trimmed)
    const response = await fetchWithAuth(`${API_URL}/v1/projects/list?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
      signal: opts?.signal,
      headers: { Accept: 'application/json' }
    })
    if (!response.ok) return []
    const json = (await response.json()) as any
    const data = Array.isArray(json?.data) ? json.data : []
    return data
      .map((p: any) => ({
        id: String(p?.id || ''),
        title: String(p?.title || '').trim(),
        updated_at: typeof p?.updated_at === 'string' ? p.updated_at : null,
      }))
      .filter((p: ProjectPickerItem) => p.id && p.title)
      .sort((a: ProjectPickerItem, b: ProjectPickerItem) => {
        const aTime = a.updated_at ? Date.parse(a.updated_at) : 0
        const bTime = b.updated_at ? Date.parse(b.updated_at) : 0
        if (aTime !== bTime) return bTime - aTime
        return b.title.localeCompare(a.title)
      })
  } catch (error) {
    if ((error as any)?.name !== 'AbortError') await handleApiError(error)
    return []
  }
}

export const getProjectById = async (projectId: string): Promise<ProjectPickerItem | undefined> => {
  const normalizedId = String(projectId || '').trim()
  if (!normalizedId) return undefined
  const cached = PROJECT_BY_ID_CACHE.get(normalizedId)
  if (cached) return cached
  const inflight = PROJECT_BY_ID_INFLIGHT.get(normalizedId)
  if (inflight) return inflight

  const request = (async (): Promise<ProjectPickerItem | undefined> => {
    try {
      if (!API_URL) return undefined
      const response = await fetchWithAuth(`${API_URL}/v1/projects/${encodeURIComponent(normalizedId)}`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' }
      })
      if (!response.ok) return undefined
      const json = (await response.json()) as any
      const project = json?.project
      const id = String(project?.id || '').trim()
      const title = String(project?.title || '').trim()
      if (!id || !title) return undefined
      const value = { id, title } as ProjectPickerItem
      PROJECT_BY_ID_CACHE.set(normalizedId, value)
      return value
    } catch (error) {
      await handleApiError(error)
      return undefined
    } finally {
      PROJECT_BY_ID_INFLIGHT.delete(normalizedId)
    }
  })()

  PROJECT_BY_ID_INFLIGHT.set(normalizedId, request)
  return request
}

export const preloadProjectById = (projectId: string): void => {
  const normalizedId = String(projectId || '').trim()
  if (!normalizedId) return
  if (PROJECT_BY_ID_CACHE.has(normalizedId)) return
  if (PROJECT_BY_ID_INFLIGHT.has(normalizedId)) return
  void getProjectById(normalizedId)
}

/** Cursor-based chat history. GET /v1/agent/chat/history?cursor=&limit= */
export const getChatsHistory = async ({
  cursor,
  limit = 10,
  projectId,
}: { cursor?: string | null; limit?: number; projectId?: string | null } = {}): Promise<ChatsHistoryPage | undefined> => {
  try {
    const safeLimit = Math.max(1, Math.min(100, Math.floor(limit || 10)))
    const params = new URLSearchParams()
    params.set('limit', String(safeLimit))
    if (cursor) params.set('cursor', cursor)
    if (projectId && projectId.trim()) params.set('project_id', projectId.trim())
    const res = await fetchWithAuth(`${API_URL}/v1/agent/chat/history?${params.toString()}`, {
      credentials: 'include'
    })
    if (!res.ok) {
      console.error('Response not ok:', res.status, res.statusText)
      return undefined
    }
    const json = (await res.json()) as any
    const data = Array.isArray(json?.data) ? json.data : []
    const next_cursor = typeof json?.next_cursor === 'string' ? json.next_cursor : null
    return { data, next_cursor }
  } catch (error) {
    console.error('Error getting chat history:', error)
    await handleApiError(error)
    return undefined
  }
}

/** GET /v1/agent/chat/<chat_id> */
export const getAgentChat = async (chatId: string): Promise<AgentChatSummary | undefined> => {
  try {
    const res = await fetchWithAuth(`${API_URL}/v1/agent/chat/${encodeURIComponent(chatId)}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' }
    })
    if (!res.ok) return undefined
    return (await res.json()) as AgentChatSummary
  } catch (error) {
    console.error('Error getting agent chat summary:', error)
    await handleApiError(error)
    return undefined
  }
}

/** PUT /v1/agent/chat/<chat_id> */
export const updateAgentChat = async (
  chatId: string,
  payload: { name?: string; organize_label?: 'pinned' | 'archive' }
): Promise<AgentChatSummary | undefined> => {
  try {
    const body: Record<string, unknown> = {}
    if (typeof payload.name === 'string') body.name = payload.name
    if (payload.organize_label) body.organize_label = payload.organize_label
    const res = await fetchWithAuth(`${API_URL}/v1/agent/chat/${encodeURIComponent(chatId)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    })
    if (!res.ok) return undefined
    return (await res.json()) as AgentChatSummary
  } catch (error) {
    console.error('Error updating agent chat summary:', error)
    await handleApiError(error)
    return undefined
  }
}

/** DELETE /v1/agent/chat/<chat_id> */
export const deleteAgentChat = async (chatId: string): Promise<boolean> => {
  try {
    const res = await fetchWithAuth(`${API_URL}/v1/agent/chat/${encodeURIComponent(chatId)}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    return res.status === 204 || res.ok
  } catch (error) {
    console.error('Error deleting agent chat summary:', error)
    await handleApiError(error)
    return false
  }
}

/**
 * Search or recent chats. GET /v1/agent/chat/history/search
 * - No q (empty or missing): returns last N updated chats for user (by updated_at desc), limit up to 50.
 * - With q (min 2 chars): filter by title (case-insensitive regex), return up to limit.
 */
export const searchChatHistory = async (
  q?: string,
  opts?: { limit?: number; signal?: AbortSignal; projectId?: string | null }
): Promise<ChatHistorySearchResponse | undefined> => {
  try {
    if (!API_URL) return undefined
    const limit = Math.min(50, Math.max(1, opts?.limit ?? 20))
    const params = new URLSearchParams()
    params.set('limit', String(limit))
    const trimmed = q?.trim() ?? ''
    if (trimmed.length >= 2) params.set('q', trimmed)
    const projectId = opts?.projectId?.trim()
    if (projectId) params.set('project_id', projectId)
    const res = await fetchWithAuth(
      `${API_URL}/v1/agent/chat/history/search?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        signal: opts?.signal,
        headers: { Accept: 'application/json' }
      }
    )
    if (!res.ok) return undefined
    return (await res.json()) as ChatHistorySearchResponse
  } catch (error) {
    if ((error as any)?.name !== 'AbortError') await handleApiError(error)
    return undefined
  }
}

export const getWorkflowsList = async ({
  limit,
  cursor,
  project_id,
  getCandidatesNumber,
  GetTargetExperiment,
  updated_from,
  updated_to,
  status,
  workflow_type,
  app_key,
  q,
}: {
  limit: number
  cursor?: string | null
  project_id?: string
  getCandidatesNumber?: boolean
  GetTargetExperiment?: boolean
  updated_from?: string
  updated_to?: string
  status?: string[] | string
  workflow_type?: string[] | string
  app_key?: string[] | string
  q?: string
}): Promise<WorkflowsListResponse | undefined> => {
  try {
    const params = new URLSearchParams()
    params.set('limit', String(limit))
    if (cursor) params.set('cursor', cursor)
    if (project_id) params.set('project_id', project_id)
    if (getCandidatesNumber) params.set('getCandidatesNumber', 'true')
    if (GetTargetExperiment) params.set('GetTargetExperiment', 'true')
    if (updated_from) params.set('updated_from', updated_from)
    if (updated_to) params.set('updated_to', updated_to)
    if (Array.isArray(status)) {
      for (const s of status) {
        const v = String(s || '').trim()
        if (!v) continue
        params.append('status', v)
      }
    } else if (status) {
      // Supports either `status=A,B` or `status=A`
      params.set('status', String(status))
    }
    if (Array.isArray(workflow_type)) {
      for (const t of workflow_type) {
        const v = String(t || '').trim()
        if (!v) continue
        params.append('workflow_type', v)
      }
    } else if (workflow_type) {
      // Supports either `workflow_type=A,B` or `workflow_type=A`
      params.set('workflow_type', String(workflow_type))
    }
    if (Array.isArray(app_key)) {
      for (const a of app_key) {
        const v = String(a || '').trim()
        if (!v) continue
        params.append('app_key', v)
      }
    } else if (app_key) {
      // Supports either `app_key=A,B` or `app_key=A`
      params.set('app_key', String(app_key))
    }
    if (q && q.trim()) {
      params.set('q', q.trim())
    }

    const response = await fetchWithAuth(`${API_URL}/v1/workflows/list?${params.toString()}`, {
      credentials: 'include'
    })

    if (!response.ok) {
      console.error('Response not ok:', response.status, response.statusText)
      return undefined
    }

    const data = (await response.json()) as WorkflowsListResponse
    return data
  } catch (error) {
    console.error('Error getting workflows list:', error)
    await handleApiError(error)
    return undefined
  }
}

export const getAppsList = async (
  opts?: { limit?: number; q?: string; signal?: AbortSignal }
): Promise<AppCatalogListItem[]> => {
  if (!API_URL) return []
  const params = new URLSearchParams()
  params.set('limit', String(Math.min(200, Math.max(1, opts?.limit ?? 200))))
  params.set('sort', 'alphabetical')
  if (opts?.q?.trim()) params.set('q', opts.q.trim())

  // Retry once for intermittent backend 5xx spikes.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetchWithAuth(`${API_URL}/v1/apps?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        signal: opts?.signal,
        headers: { Accept: 'application/json' }
      })
      if (!response.ok) {
        if (response.status >= 500 && attempt === 0) continue
        return []
      }
      const json = (await response.json()) as { data?: AppCatalogListItem[] } | null
      return Array.isArray(json?.data) ? json!.data : []
    } catch (error) {
      if ((error as any)?.name === 'AbortError') return []
      if (attempt === 0) continue
      await handleApiError(error)
      return []
    }
  }
  return []
}

export const runWorkflowHealthCheck = async (workflowId: string): Promise<boolean> => {
  try {
    const response = await fetchWithAuth(`${API_URL}/v1/workflows/run-health-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ workflow_id: workflowId })
    })
    return response.ok
  } catch (error) {
    console.error('Error running workflow health check:', error)
    await handleApiError(error)
    return false
  }
}

export const saveNewChatHistory = async (
  newChatHistory: NewChatHistory
): Promise<ChatHistory | undefined> => {
  try {
    const response = await fetchWithAuth(`${API_URL}/v1/agent/chat/history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(newChatHistory)
    })

    if (!response.ok) {
      throw new Error('Failed to save chat history')
    }

    const history = (await response.json()) as ChatHistory
    return history
  } catch (error) {
    console.error('Error saving chat history:', error)
    await handleApiError(error)
  }
}

export const getChatHistory = async (
  chatId: string
): Promise<ChatHistory | undefined> => {
  try {
    const response = await fetchWithAuth(`${API_URL}/v1/agent/chat/history/${chatId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error('Failed to get chat history')
    }

    const history = (await response.json()) as ChatHistory
    return history
  } catch (error) {
    console.error('Error getting chat history:', error)
    await handleApiError(error)
  }
}

export type AgentIdeSessionUploadFile = {
  file_name: string
  path?: string
  original_name?: string
  content_type?: string
  size?: number
  s3_key?: string
  uploaded_at?: string
  local_path?: string
  url?: string | null
}

export type AgentIdeSessionWorkspaceFile = {
  file_name: string
  path: string
  content_type?: string
  size?: number
  s3_key?: string
  url?: string | null
}

export type AgentIdeSessionFilesResponse = {
  session_id: string
  root: string
  workspace_root: string
  uploads: AgentIdeSessionUploadFile[]
  upload_folders?: string[]
  workspace: AgentIdeSessionWorkspaceFile[] | string[]
}

export type AgentIdeUploadResponse = {
  session_id: string
  uploaded: Array<{
    file_name: string
    path?: string
    original_name?: string
    content_type?: string
    size?: number
    s3_key?: string
    uploaded_at?: string
    local_path?: string
  }>
  count: number
}

export const getAgentIdeSessionFiles = async (
  sessionId: string
): Promise<AgentIdeSessionFilesResponse | undefined> => {
  try {
    if (!sessionId) return undefined
    const response = await fetchWithAuth(
      `${API_URL}/v1/agent-ide/files?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' }
      }
    )
    if (!response.ok) {
      throw new Error('Failed to get agent IDE session files')
    }
    return (await response.json()) as AgentIdeSessionFilesResponse
  } catch (error) {
    console.error('Error getting agent IDE session files:', error)
    await handleApiError(error)
  }
}

export const getAgentIdeProjectFiles = async (
  projectId: string
): Promise<AgentIdeSessionFilesResponse | undefined> => {
  try {
    if (!projectId) return undefined
    const response = await fetchWithAuth(
      `${API_URL}/v1/agent-ide/files/project?project_id=${encodeURIComponent(projectId)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' }
      }
    )
    if (!response.ok) {
      throw new Error('Failed to get agent IDE project files')
    }
    return (await response.json()) as AgentIdeSessionFilesResponse
  } catch (error) {
    console.error('Error getting agent IDE project files:', error)
    await handleApiError(error)
  }
}

export const uploadAgentIdeSessionFile = async (
  sessionId: string,
  file: File,
  relativePath?: string
): Promise<AgentIdeUploadResponse | undefined> => {
  try {
    if (!sessionId || !file) return undefined
    const form = new FormData()
    form.append('file', file)
    if (relativePath) form.append('relative_path', relativePath)

    const response = await fetchWithAuth(
      `${API_URL}/v1/agent-ide/upload?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: 'POST',
        credentials: 'include',
        body: form
      }
    )
    if (!response.ok) {
      throw new Error('Failed to upload session file')
    }
    return (await response.json()) as AgentIdeUploadResponse
  } catch (error) {
    console.error('Error uploading agent IDE session file:', error)
    await handleApiError(error)
  }
}

export const createAgentIdeSessionFolder = async (
  sessionId: string,
  folderPath: string
): Promise<{ session_id: string; folder_path: string } | undefined> => {
  try {
    if (!sessionId || !folderPath.trim()) return undefined
    const response = await fetchWithAuth(
      `${API_URL}/v1/agent-ide/folders?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ folder_path: folderPath })
      }
    )
    if (!response.ok) throw new Error('Failed to create folder')
    return (await response.json()) as { session_id: string; folder_path: string }
  } catch (error) {
    console.error('Error creating agent IDE folder:', error)
    await handleApiError(error)
  }
}

export const moveAgentIdeSessionFile = async (
  sessionId: string,
  sourcePath: string,
  targetFolder: string
): Promise<{ session_id: string; source_path: string; target_path: string } | undefined> => {
  try {
    if (!sessionId || !sourcePath.trim() || !targetFolder.trim()) return undefined
    const response = await fetchWithAuth(
      `${API_URL}/v1/agent-ide/files/move?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ source_path: sourcePath, target_folder: targetFolder })
      }
    )
    if (!response.ok) throw new Error('Failed to move file')
    return (await response.json()) as { session_id: string; source_path: string; target_path: string }
  } catch (error) {
    console.error('Error moving agent IDE file:', error)
    await handleApiError(error)
  }
}

export const renameAgentIdeSessionFile = async (
  sessionId: string,
  sourcePath: string,
  newName: string
): Promise<{ session_id: string; source_path: string; target_path: string; file_name: string } | undefined> => {
  try {
    if (!sessionId || !sourcePath.trim() || !newName.trim()) return undefined
    const response = await fetchWithAuth(
      `${API_URL}/v1/agent-ide/files/rename?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ source_path: sourcePath, new_name: newName })
      }
    )
    if (!response.ok) throw new Error('Failed to rename file')
    return (await response.json()) as { session_id: string; source_path: string; target_path: string; file_name: string }
  } catch (error) {
    console.error('Error renaming agent IDE file:', error)
    await handleApiError(error)
  }
}

export const deleteAgentIdeSessionFile = async (
  sessionId: string,
  sourcePath: string
): Promise<{ session_id: string; deleted_path: string } | undefined> => {
  try {
    if (!sessionId || !sourcePath.trim()) return undefined
    const response = await fetchWithAuth(
      `${API_URL}/v1/agent-ide/files/delete?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ source_path: sourcePath })
      }
    )
    if (!response.ok) throw new Error('Failed to delete file')
    return (await response.json()) as { session_id: string; deleted_path: string }
  } catch (error) {
    console.error('Error deleting agent IDE file:', error)
    await handleApiError(error)
  }
}

export const updateChatHistory = async (
  chatId: string,
  updatedHistory: UpdateChatHistory
): Promise<void> => {
  try {
    // Note: updatedHistory.messages now contains only NEW messages to append,
    // not the entire updated messages array. The backend should append these
    // to the existing messages rather than replacing them.
    // 
    // Similarly, updatedHistory.executions should contain only NEW executions to append,
    // not the entire updated executions array. The backend should append these
    // to the existing executions rather than replacing them.
    const response = await fetchWithAuth(`${API_URL}/v1/agent/chat/history/${chatId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(updatedHistory)
    })

    if (!response.ok) {
      throw new Error('Failed to update chat history')
    }
  } catch (error) {
    console.error('Error updating chat history:', error)
    await handleApiError(error)
  }
}

export const getPublicChatHistory = async (
  chatId: string
): Promise<ChatHistory | undefined> => {
  try {
    const response = await fetch(`${API_URL}/v1/chats/public-link/${chatId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
      // No credentials required for public endpoint
    })

    if (!response.ok) {
      throw new Error('Failed to get public chat history')
    }

    const history = (await response.json()) as ChatHistory
    return history
  } catch (error) {
    console.error('Error getting public chat history:', error)
  }
}

export async function getChatPublicStatus(chatId: string): Promise<{ id: string; isPublic: boolean } | null> {
  try {
    const API_URL = process.env.NEXT_PUBLIC_API_URL
    const response = await fetchWithAuth(`${API_URL}/v1/chats/public-link/get/${chatId}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Failed to get chat public status:', error)
    await handleApiError(error)
    return null
  }
}

export async function updateChatPublicStatus(chatId: string, isPublic: boolean): Promise<{ id: string; isPublic: boolean } | null> {
  try {
    const API_URL = process.env.NEXT_PUBLIC_API_URL
    const response = await fetchWithAuth(`${API_URL}/v1/chats/public-link/create`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        isPublic: isPublic,
        chatId: chatId
      }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Failed to update chat public status:', error)
    await handleApiError(error)
    return null
  }
}

// Create library item
export const createLibraryItem = async (
  name: string,
  type: 'folder' | 'file',
  parentId?: string
): Promise<CreateLibraryItemResult> => {
  try {
    const body: Record<string, unknown> = { name, type }
    if (parentId) body.parent_id = parentId
    const response = await fetchWithAuth(`${API_URL}/v1/library/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    })
    if (!response.ok) {
      if (response.status === 403) {
        try {
          const payload = (await response.clone().json()) as { message?: string } | null
          const message = String(payload?.message || '').toLowerCase()
          if (message.includes('read-only permission for this team')) {
            return { readOnlyDenied: true }
          }
        } catch {
          // ignore non-JSON 403 payloads
        }
      }
      console.error('Failed to create item:', response.status, response.statusText)
      return {}
    }
    const created = await response.json()
    return { item: created as LibraryItem }
  } catch (error) {
    console.error('Error creating item:', error)
    await handleApiError(error)
    return {}
  }
}

export const createLibraryEntity = async (
  payload: Record<string, unknown>,
  parentId?: string,
  options?: { throwOnReadOnlyDenied?: boolean }
): Promise<LibraryItem | undefined> => {
  try {
    const body: Record<string, unknown> = { ...payload }
    if (parentId) body.parent_id = parentId
    const response = await fetchWithAuth(`${API_URL}/v1/library/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    })
    if (!response.ok) {
      if (response.status === 403 && options?.throwOnReadOnlyDenied) {
        try {
          const payload = (await response.clone().json()) as { message?: string } | null
          const message = String(payload?.message || '').toLowerCase()
          if (message.includes('read-only permission for this team')) {
            throw new ReadOnlyTeamDeniedError()
          }
        } catch (error) {
          if (isReadOnlyTeamDeniedError(error)) throw error
          // ignore parse failures and continue with generic handling
        }
      }
      console.error('Failed to create file entity:', response.status, response.statusText)
      return undefined
    }
    const created = await response.json()
    return created as LibraryItem
  } catch (error) {
    if (options?.throwOnReadOnlyDenied && isReadOnlyTeamDeniedError(error)) {
      throw error
    }
    console.error('Error creating file entity:', error)
    await handleApiError(error)
    return undefined
  }
}

export const uploadLibraryFile = async (
  itemId: string,
  file: File,
  additionalFields?: Record<string, unknown>
): Promise<boolean> => {
  try {
    const form = new FormData()
    // Backend expects 'files' field (supports single-file as well)
    form.append('files', file)
    // Append any additional dynamic fields (e.g., pae_json)
    if (additionalFields) {
      for (const [key, value] of Object.entries(additionalFields)) {
        if (value === undefined || value === null) continue
        if (typeof value === 'string') {
          form.append(key, value)
        } else if (value instanceof Blob) {
          form.append(key, value)
        } else {
          form.append(key, JSON.stringify(value))
        }
      }
    }
    const response = await fetchWithAuth(`${API_URL}/v1/library/${itemId}/upload-files`, {
      method: 'POST',
      credentials: 'include',
      body: form
    })
    if (!response.ok) {
      console.error('Failed to upload file:', response.status, response.statusText)
      return false
    }
    return true
  } catch (error) {
    console.error('Error uploading file:', error)
    await handleApiError(error)
    return false
  }
}

export const createLibraryFile = async (
  payload: Record<string, unknown>,
  parentId?: string
): Promise<boolean> => {
  try {
    const body: Record<string, unknown> = { ...payload }
    if (parentId) body.parent_id = parentId
    const response = await fetchWithAuth(`${API_URL}/v1/library/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    })
    if (!response.ok) {
      console.error('Failed to create file entity:', response.status, response.statusText)
      return false
    }
    return true
  } catch (error) {
    console.error('Error creating file entity:', error)
    await handleApiError(error)
    return false
  }
}

export const getLibraryList = async ({
  limit,
  cursor,
  updated_from,
  updated_to,
  file_type,
}: {
  limit: number
  cursor?: string | null
  updated_from?: string
  updated_to?: string
  file_type?: string[] | string
}): Promise<LibraryListResponse | undefined> => {
  try {
    const params = new URLSearchParams()
    params.set('limit', String(limit))
    if (cursor) params.set('cursor', cursor)
    if (updated_from) params.set('updated_from', updated_from)
    if (updated_to) params.set('updated_to', updated_to)
    if (Array.isArray(file_type)) {
      for (const t of file_type) {
        const v = String(t || '').trim()
        if (!v) continue
        params.append('file_type', v)
      }
    } else if (file_type) {
      // Supports either `file_type=A,B` or `file_type=A`
      params.set('file_type', String(file_type))
    }

    const response = await fetchWithAuth(`${API_URL}/v1/library/list?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    })

    if (!response.ok) {
      console.error('Failed to get library list:', response.status, response.statusText)
      return undefined
    }

    const data = (await response.json()) as LibraryListResponse
    return data
  } catch (error) {
    console.error('Error getting library list:', error)
    await handleApiError(error)
    return undefined
  }
}

export const searchLibrary = async (
  q: string,
  opts?: { signal?: AbortSignal }
): Promise<LibrarySearchResponse | undefined> => {
  try {
    if (!API_URL) return undefined
    const response = await fetchWithAuth(`${API_URL}/v1/library/search?q=${encodeURIComponent(q)}`, {
      method: 'GET',
      credentials: 'include',
      signal: opts?.signal,
      headers: { 'Accept': 'application/json' }
    })
    if (!response.ok) {
      console.error('Failed to search library:', response.status, response.statusText)
      return undefined
    }
    const data = (await response.json()) as LibrarySearchResponse
    return data
  } catch (error) {
    // Abort is expected during debounced typeahead
    if ((error as any)?.name !== 'AbortError') {
      console.error('Error searching library:', error)
      await handleApiError(error)
    }
    return undefined
  }
}

export const searchLibraryRecent = async (
  opts?: { signal?: AbortSignal }
): Promise<LibrarySearchResponse | undefined> => {
  try {
    if (!API_URL) return undefined
    const response = await fetchWithAuth(`${API_URL}/v1/library/search?recent=true`, {
      method: 'GET',
      credentials: 'include',
      signal: opts?.signal,
      headers: { 'Accept': 'application/json' }
    })
    if (!response.ok) {
      console.error('Failed to search recent library items:', response.status, response.statusText)
      return undefined
    }
    const data = (await response.json()) as LibrarySearchResponse
    return data
  } catch (error) {
    // Abort is expected during typeahead / focus changes
    if ((error as any)?.name !== 'AbortError') {
      console.error('Error searching recent library items:', error)
      await handleApiError(error)
    }
    return undefined
  }
}

export const renameLibraryItem = async (id: string, name: string): Promise<boolean> => {
  try {
    const response = await fetchWithAuth(`${API_URL}/v1/library/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ name })
    })

    if (!response.ok) {
      console.error('Failed to rename item:', response.status, response.statusText)
      return false
    }
    return true
  } catch (error) {
    console.error('Error renaming item:', error)
    await handleApiError(error)
    return false
  }
}

export const updateLibraryItemColor = async (id: string, colorHex: string | null): Promise<boolean> => {
  try {
    const response = await fetchWithAuth(`${API_URL}/v1/library/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ colorHex })
    })

    if (!response.ok) {
      console.error('Failed to update color:', response.status, response.statusText)
      return false
    }
    return true
  } catch (error) {
    console.error('Error updating color:', error)
    await handleApiError(error)
    return false
  }
}

export const deleteLibraryItem = async (id: string): Promise<boolean> => {
  try {
    const response = await fetchWithAuth(`${API_URL}/v1/library/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (!response.ok) {
      console.error('Failed to delete item:', response.status, response.statusText)
      return false
    }
    return true
  } catch (error) {
    console.error('Error deleting item:', error)
    await handleApiError(error)
    return false
  }
}

export const deleteLibraryItemsBatch = async (ids: string[]): Promise<boolean> => {
  try {
    const response = await fetchWithAuth(`${API_URL}/v1/library/batch-delete`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(ids)
    })
    if (!response.ok) {
      console.error('Failed to batch delete:', response.status, response.statusText)
      return false
    }
    return true
  } catch (error) {
    console.error('Error batch deleting:', error)
    await handleApiError(error)
    return false
  }
}

export const getLibraryFileUrl = async (
  itemId: string,
  fileName: string,
  opts?: { shared?: boolean; workflowId?: string | null; teamId?: string | null }
): Promise<string | undefined> => {
  try {
    const params = new URLSearchParams()
    if (opts?.teamId) params.set('team_id', String(opts.teamId))
    if (opts?.shared) params.set('shared', 'true')
    if (opts?.workflowId) params.set('workflow_id', String(opts.workflowId))
    const qs = params.toString()
    const url = `${API_URL}/v1/library/file/${itemId}/${encodeURIComponent(fileName)}${qs ? `?${qs}` : ''}`

    let response: Response
    if (opts?.shared) {
      response = await fetch(url, {
        method: 'GET',
        credentials: 'omit',
        headers: { 'Accept': 'application/json' }
      })
      if (!response.ok && (response.status === 401 || response.status === 403)) {
        response = await fetchWithAuth(url, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Accept': 'application/json' }
        }, undefined, { skipUnauthorizedRedirect: true })
      }
    } else {
      response = await fetchWithAuth(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      }, undefined, { skipUnauthorizedRedirect: true })
    }
    if (!response.ok) {
      console.error('Failed to get library file URL:', response.status, response.statusText)
      return undefined
    }
    const json = await response.json()
    // API returns a JSON string like "https://..."
    if (typeof json === 'string') return json
    // Fallback if wrapped
    if (json && typeof json.url === 'string') return json.url
    return undefined
  } catch (error) {
    console.error('Error fetching library file URL:', error)
    await handleApiError(error)
    return undefined
  }
}

// Global request cache to prevent duplicate API calls for the same resource
const libraryItemRequestCache = new Map<string, Promise<LibraryItem | undefined>>()

export const getLibraryItem = async (
  id: string,
  opts?: { includePaeJson?: boolean; shared?: boolean; workflowId?: string | null; teamId?: string | null }
): Promise<LibraryItem | undefined> => {
  // Create a cache key that includes both id and options
  const cacheKey = [
    id,
    opts?.includePaeJson ? 'pae=1' : 'pae=0',
    opts?.shared ? 'shared=1' : 'shared=0',
    opts?.workflowId ? `workflow=${opts.workflowId}` : 'workflow=',
    opts?.teamId ? `team=${opts.teamId}` : 'team='
  ].join('|')
  
  // Check if there's already a request in flight for this exact resource
  const existingRequest = libraryItemRequestCache.get(cacheKey)
  if (existingRequest) {
    // Return the existing promise instead of making a new request
    return existingRequest
  }
  
  // Build the promise via async IIFE to avoid unassigned resolver variables
  const requestPromise: Promise<LibraryItem | undefined> = (async () => {
    try {
      const params = new URLSearchParams()
      if (opts?.includePaeJson) params.set('fetch_PAE', 'true')
      if (opts?.teamId) params.set('team_id', String(opts.teamId))
      if (opts?.shared) params.set('shared', 'true')
      if (opts?.workflowId) params.set('workflow_id', String(opts.workflowId))
      const qs = params.toString()
      const url = `${API_URL}/v1/library/${id}${qs ? `?${qs}` : ''}`

      let response: Response
      if (opts?.shared) {
        response = await fetch(url, {
          method: 'GET',
          credentials: 'omit',
          headers: { 'Accept': 'application/json' }
        })
        if (!response.ok && (response.status === 401 || response.status === 403)) {
          response = await fetchWithAuth(url, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Accept': 'application/json' }
          }, undefined, { skipUnauthorizedRedirect: true })
        }
      } else {
        response = await fetchWithAuth(url, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Accept': 'application/json' }
        }, undefined, { skipUnauthorizedRedirect: true })
      }
      if (!response.ok) {
        console.error('Failed to get library item:', response.status, response.statusText)
        return undefined
      }
      const json = await response.json()
      return json as LibraryItem
    } catch (error) {
      console.error('Error fetching library item:', error)
      await handleApiError(error)
      throw error
    } finally {
      // Remove from cache after request completes (success or error)
      libraryItemRequestCache.delete(cacheKey)
    }
  })()

  // Store the promise in cache IMMEDIATELY (synchronously) to dedupe concurrent callers
  libraryItemRequestCache.set(cacheKey, requestPromise)

  return requestPromise
}
